import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { formatPrice, statusColor, statusLabel } from '../../utils/helpers';
import toast from 'react-hot-toast';
import './AdminOrders.css';

const STATUSES = ['', 'processing', 'sent', 'delivered', 'paid', 'cancelled'];

const AdminOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const today = new Date();
  const [filterDay, setFilterDay] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(String(today.getFullYear()));
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterDay) params.set('day', filterDay);
    if (filterMonth) params.set('month', filterMonth);
    if (filterYear) params.set('year', filterYear);
    if (filterStatus) params.set('status', filterStatus);

    api.get(`/orders?${params}`).then(({ data }) => {
      setOrders(data.orders);
      setTotalRevenue(data.totalRevenue);
      setTotalOrders(data.totalOrders);
    }).finally(() => setLoading(false));
  }, [filterDay, filterMonth, filterYear, filterStatus]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterDay) params.set('day', filterDay);
      if (filterMonth) params.set('month', filterMonth);
      if (filterYear) params.set('year', filterYear);
      if (filterStatus) params.set('status', filterStatus);

      const token = JSON.parse(localStorage.getItem('norixUser'))?.token;

      // Use REACT_APP_API_URL so it works in production (Cloudflare → Render)
      const baseURL = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
        : '';
      const res = await fetch(`${baseURL}/api/admin/orders/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Server error ${res.status}`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `norix-orders-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, orderStatus: status } : o));
      toast.success(`Status updated to ${status}`);
    } catch {
      toast.error('Update failed');
    }
  };

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      <div className="orders-admin-header">
        <div>
          <div className="admin-page-title">Orders</div>
          <p className="admin-page-sub">Manage and track all customer orders</p>
        </div>
        <button
          className="btn btn-primary ed-btn"
          onClick={exportPDF}
          disabled={exporting}
          title="Export & Download all filtered orders as PDF"
        >
          {exporting ? '⏳' : '📄'} ED – Export PDF
        </button>
      </div>

      {/* Stat Cards */}
      <div className="orders-stats">
        <div className="ostat-card purple">
          <span>📦</span>
          <div>
            <div className="ostat-val">{totalOrders}</div>
            <div className="ostat-label">Total Orders</div>
          </div>
        </div>
        <div className="ostat-card pink">
          <span>💰</span>
          <div>
            <div className="ostat-val">{formatPrice(totalRevenue)}</div>
            <div className="ostat-label">Total Revenue</div>
          </div>
        </div>
        <div className="ostat-card yellow">
          <span>⏳</span>
          <div>
            <div className="ostat-val">{orders.filter(o => o.orderStatus === 'processing').length}</div>
            <div className="ostat-label">Processing</div>
          </div>
        </div>
        <div className="ostat-card green">
          <span>✅</span>
          <div>
            <div className="ostat-val">{orders.filter(o => o.orderStatus === 'paid').length}</div>
            <div className="ostat-label">Paid</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters orders-filters">
        <div className="filter-group">
          <span className="filter-label">Day:</span>
          <input
            type="number" min={1} max={31}
            className="filter-select"
            style={{ width: 70 }}
            placeholder="DD"
            value={filterDay}
            onChange={e => setFilterDay(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <span className="filter-label">Month:</span>
          <select className="filter-select" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            <option value="">All</option>
            {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <span className="filter-label">Year:</span>
          <select className="filter-select" value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <span className="filter-label">Status:</span>
          <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s || 'All Status'}</option>)}
          </select>
        </div>
        <button
          className="btn btn-outline"
          style={{ padding: '8px 16px', fontSize: '0.82rem' }}
          onClick={() => { setFilterDay(''); setFilterMonth(''); setFilterYear(String(today.getFullYear())); setFilterStatus(''); }}
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        <div className="table-header">
          <span className="table-title">{totalOrders} Orders Found</span>
          <button className="btn btn-outline ed-btn-sm" onClick={exportPDF} disabled={exporting}>
            📄 ED — Download List
          </button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <div className="orders-table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Shipping</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order._id}>
                    <td>
                      <Link to={`/admin/orders/${order._id}`} className="order-id-link">
                        #{order._id.slice(-6).toUpperCase()}
                      </Link>
                      {order.isPreOrder && <span className="preorder-mini">PRE</span>}
                      {order.customerMarkedPaid && !order.adminVerifiedPaid && (
                        <span className="verify-badge" title="Customer marked as paid – verify!">⚠️</span>
                      )}
                    </td>
                    <td className="customer-cell">
                      <strong>{order.user?.name}</strong>
                      <span>{order.user?.email}</span>
                    </td>
                    <td>{order.user?.phone || order.shippingAddress?.phone || '—'}</td>
                    <td>{order.items?.length} item{order.items?.length > 1 ? 's' : ''}</td>
                    <td className="price-cell">{formatPrice(order.totalPrice)}</td>
                    <td>{order.shippingAddress?.insideDhaka ? '🏙️ Dhaka' : '🌍 Outside'}</td>
                    <td><span className={`badge ${statusColor(order.paymentStatus)}`}>{statusLabel(order.paymentStatus)}</span></td>
                    <td>
                      <select
                        className="status-select"
                        value={order.orderStatus}
                        onChange={e => updateStatus(order._id, e.target.value)}
                      >
                        <option value="processing">Processing</option>
                        <option value="sent">Sent</option>
                        <option value="delivered">Delivered</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="date-cell">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-btns">
                        <Link to={`/admin/orders/${order._id}`} className="action-btn view" title="View Details">👁</Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <div className="table-empty">No orders found for the selected filters.</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
